import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Card, Badge, Button } from '../components/Shared';
import { VendorIcon, Avatar } from '../components/Branding';
import { vendors } from '../data/mock';
import SafeIcon from '../common/SafeIcon';
import { FiDownload, FiSearch, FiInfo, FiPlus, FiArrowRight, FiExternalLink, FiMail } from 'react-icons/fi';

const Vendors = () => {
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('All');
  const [selectedVendor, setSelectedVendor] = useState(null);

  const filteredVendors = vendors.filter(v => {
    const matchesSearch = v.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'All' || v.status === filter;
    return matchesSearch && matchesFilter;
  });

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="p-8 max-w-[1600px] mx-auto space-y-8">
      <div className="flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-white tracking-tight">Supplier Ecosystem</h1>
          <p className="text-slate-500 font-medium mt-1">Centralized directory of all active and onboarding partners.</p>
        </div>
        <div className="flex gap-3 w-full lg:w-auto">
          <Button variant="secondary" className="flex-1 lg:flex-none flex items-center justify-center gap-2">
            <SafeIcon icon={FiDownload} /> EXPORT CSV
          </Button>
          <Button className="flex-1 lg:flex-none flex items-center justify-center gap-2">
            <SafeIcon icon={FiPlus} /> ADD SUPPLIER
          </Button>
        </div>
      </div>

      <div className="flex flex-col md:flex-row gap-4 items-center bg-[#0F1626]/50 p-4 rounded-2xl border border-slate-800">
        <div className="relative flex-1 w-full group">
          <SafeIcon icon={FiSearch} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500 group-focus-within:text-yellow-400 transition-colors" />
          <input 
            type="text" 
            placeholder="Search by name, category, or tax ID..." 
            className="w-full bg-[#0A0F1C] border border-slate-700/50 rounded-xl pl-12 pr-4 py-3 text-white focus:border-yellow-400/50 outline-none transition-all placeholder-slate-600"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="flex gap-2 w-full md:w-auto overflow-x-auto pb-1 md:pb-0 hide-scrollbar">
          {['All', 'Active', 'Under Review', 'Onboarding'].map(s => (
            <button 
              key={s}
              onClick={() => setFilter(s)}
              className={`px-5 py-2.5 rounded-xl text-xs font-black tracking-widest uppercase transition-all whitespace-nowrap border ${
                filter === s 
                  ? 'bg-yellow-400 text-black border-yellow-400 shadow-[0_0_15px_rgba(250,204,21,0.2)]' 
                  : 'bg-slate-800/40 text-slate-500 border-slate-700 hover:border-slate-500'
              }`}
            >
              {s}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
        <Card className="lg:col-span-8 overflow-hidden border-slate-800/50">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#0A0F1C] border-b border-slate-800 text-slate-500 text-[10px] uppercase font-black tracking-[0.2em]">
                  <th className="p-5">Partner Profile</th>
                  <th className="p-5">Risk Matrix</th>
                  <th className="p-5">Annual Spend</th>
                  <th className="p-5">Lifecycle</th>
                  <th className="p-5 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-800/30">
                {filteredVendors.length > 0 ? (
                  filteredVendors.map((vendor) => (
                    <tr 
                      key={vendor.id} 
                      onClick={() => setSelectedVendor(vendor)}
                      className={`hover:bg-yellow-400/[0.02] transition-all group cursor-pointer ${selectedVendor?.id === vendor.id ? 'bg-yellow-400/5' : ''}`}
                    >
                      <td className="p-5">
                        <div className="flex items-center gap-4">
                          <VendorIcon name={vendor.name} />
                          <div>
                            <p className="text-white font-bold group-hover:text-yellow-400 transition-colors leading-tight">{vendor.name}</p>
                            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-wider mt-1">{vendor.category}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-5"><Badge type={vendor.risk}>{vendor.risk}</Badge></td>
                      <td className="p-5 text-slate-300 font-mono text-sm font-semibold">{vendor.spend}</td>
                      <td className="p-5">
                        <div className="flex items-center gap-2 text-xs font-bold text-slate-400">
                          <div className={`w-2 h-2 rounded-full ${vendor.status === 'Active' ? 'bg-emerald-400 shadow-[0_0_8px_#10b981]' : 'bg-yellow-400 animate-pulse'}`}></div>
                          {vendor.status}
                        </div>
                      </td>
                      <td className="p-5 text-right">
                        <button className="p-2 text-slate-600 hover:text-yellow-400 transition-colors">
                          <SafeIcon icon={FiExternalLink} />
                        </button>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="p-20 text-center">
                      <div className="flex flex-col items-center gap-4 opacity-20">
                        <SafeIcon icon={FiSearch} className="text-6xl text-slate-500" />
                        <p className="font-black uppercase tracking-widest text-slate-400">No matches found</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Dynamic Detail Panel */}
        <div className="lg:col-span-4 lg:sticky lg:top-28">
          <AnimatePresence mode="wait">
            {selectedVendor ? (
              <motion.div
                key={selectedVendor.id}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 10 }}
              >
                <Card className="p-8 border-yellow-400/20 bg-gradient-to-br from-[#0F1626] to-[#0A0F1C] shadow-2xl">
                  <div className="flex justify-between items-start mb-8">
                    <Badge type="default">Partner ID: {selectedVendor.id}</Badge>
                    <button onClick={() => setSelectedVendor(null)} className="text-slate-600 hover:text-white transition-colors">
                      <SafeIcon icon={FiPlus} className="rotate-45 text-xl" />
                    </button>
                  </div>
                  
                  <div className="space-y-8">
                    <div className="flex items-center gap-5">
                      <div className="w-16 h-16 rounded-2xl bg-yellow-400 flex items-center justify-center text-black font-black text-3xl shadow-lg shadow-yellow-400/20">
                        {selectedVendor.name.charAt(0)}
                      </div>
                      <div>
                        <h4 className="text-2xl font-black text-white tracking-tight">{selectedVendor.name}</h4>
                        <p className="text-xs font-bold text-slate-500 uppercase tracking-widest">Onboarded Jan 2024</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div className="p-4 bg-slate-900/50 rounded-2xl border border-slate-800">
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.1em] mb-1">Impact Score</p>
                        <div className="flex items-end gap-1">
                          <span className="text-2xl font-black text-yellow-400">{selectedVendor.value}</span>
                          <span className="text-xs text-slate-600 mb-1.5 font-bold">/100</span>
                        </div>
                      </div>
                      <div className="p-4 bg-slate-900/50 rounded-2xl border border-slate-800">
                        <p className="text-[10px] font-black text-slate-500 uppercase tracking-[0.1em] mb-1">Risk Index</p>
                        <div className="flex items-end gap-1">
                          <span className="text-2xl font-black text-red-500">{selectedVendor.riskScore}</span>
                          <span className="text-xs text-slate-600 mb-1.5 font-bold">/100</span>
                        </div>
                      </div>
                    </div>

                    <div className="space-y-3">
                      <h5 className="text-[10px] font-black text-slate-400 uppercase tracking-widest">Executive Summary</h5>
                      <div className="p-5 bg-slate-900/80 rounded-2xl border border-slate-800 text-sm text-slate-300 italic leading-relaxed shadow-inner">
                        "{selectedVendor.notes}"
                      </div>
                    </div>

                    <div className="pt-4 space-y-3">
                      <Button className="w-full flex items-center justify-center gap-3 py-4">
                        <SafeIcon icon={FiArrowRight} /> FULL AUDIT REPORT
                      </Button>
                      <div className="flex gap-3">
                        <button className="flex-1 flex items-center justify-center gap-2 p-3 bg-slate-800/40 hover:bg-slate-800 text-slate-400 hover:text-white text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-700 transition-all">
                          <SafeIcon icon={FiMail} /> MESSAGE
                        </button>
                        <button className="flex-1 flex items-center justify-center gap-2 p-3 bg-slate-800/40 hover:bg-slate-800 text-slate-400 hover:text-white text-[10px] font-black uppercase tracking-widest rounded-xl border border-slate-700 transition-all">
                          <SafeIcon icon={FiExternalLink} /> PORTAL
                        </button>
                      </div>
                    </div>
                  </div>
                </Card>
              </motion.div>
            ) : (
              <div className="h-full min-h-[400px] border-2 border-dashed border-slate-800/50 rounded-3xl flex flex-col items-center justify-center text-center p-12 bg-slate-900/10">
                <div className="w-16 h-16 rounded-full bg-slate-800/50 flex items-center justify-center mb-6">
                  <SafeIcon icon={FiInfo} className="text-3xl text-slate-600" />
                </div>
                <h4 className="text-slate-400 font-black uppercase tracking-widest text-sm mb-2">Selection Required</h4>
                <p className="text-slate-600 text-xs font-medium leading-relaxed">Select a supplier from the directory to initialize the intelligence profile and risk assessment.</p>
              </div>
            )}
          </AnimatePresence>
        </div>
      </div>
    </motion.div>
  );
};

export default Vendors;